import React, { useState, useEffect, useCallback } from "react";
import {
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimes,
  FaEye,
  FaSearch,
  FaFilter,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaDollarSign,
} from "react-icons/fa";
import { getAllStudents, updateStudent } from "../utils/emailUtils";
import { logAdminActivity } from "../utils/adminSecurity";
import "./AccountingSection.css";

const AccountingSection = () => {
  const [students, setStudents] = useState([]);
  const [accountingData, setAccountingData] = useState([]);
  const [filteredAccounting, setFilteredAccounting] = useState([]);
  const [completedPayments, setCompletedPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    loadAccountingData();
  }, []);

  useEffect(() => {
    filterAccountingData();
  }, [accountingData, searchTerm, paymentFilter]);

  const loadAccountingData = async () => {
    setIsLoading(true);
    try {
      const studentData = await getAllStudents();
      setStudents(studentData);

      // Convert students to accounting format
      const accounting = studentData
        .filter(student => student.agreedPrice && parseFloat(student.agreedPrice) > 0)
        .map(student => {
          const agreedPrice = parseFloat(student.agreedPrice);
          const installmentCount = parseInt(student.installmentCount || "1");
          const installmentAmount = agreedPrice / installmentCount;

          // Create installment structure - check for existing payment data
          const existingPayments = student.paymentData || {};
          const installments = Array.from({ length: installmentCount }, (_, index) => {
            const installmentKey = `installment_${index + 1}`;
            const existingPayment = existingPayments[installmentKey];
            return {
              number: index + 1,
              amount: installmentAmount,
              paid: existingPayment?.paid || false,
              paidDate: existingPayment?.paidDate || null,
            };
          });

          // Calculate totals based on existing payments
          const totalPaid = installments.reduce(
            (sum, inst) => sum + (inst.paid ? inst.amount : 0),
            0
          );
          const remainingAmount = agreedPrice - totalPaid;
          const paidCount = installments.filter(inst => inst.paid).length;

          let paymentStatus = "pending";
          if (paidCount === installments.length) {
            paymentStatus = "completed";
          } else if (paidCount > 0) {
            paymentStatus = "partial";
          }

          return {
            id: student.id,
            studentName: `${student.firstName} ${student.lastName}`,
            agreedPrice: agreedPrice,
            installmentCount: installmentCount,
            installments: installments,
            totalPaid: totalPaid,
            remainingAmount: remainingAmount,
            paymentStatus: paymentStatus,
            registrationDate: student.registrationDate,
          };
        });

      setAccountingData(accounting);
    } catch (error) {
      console.error("Error loading accounting data:", error);
    }
    setIsLoading(false);
  };

  const filterAccountingData = useCallback(() => {
    let allData = [...accountingData];

    // Search filter
    if (searchTerm) {
      allData = allData.filter(account =>
        account.studentName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Separate completed and pending/partial payments
    const completed = allData.filter(account => account.paymentStatus === "completed");
    const pendingPartial = allData.filter(account => account.paymentStatus !== "completed");

    // Apply payment status filter to pending/partial only
    let filteredPending = [...pendingPartial];
    if (paymentFilter !== "all") {
      filteredPending = filteredPending.filter(account => account.paymentStatus === paymentFilter);
    }

    // Sort both by registration date (newest first)
    filteredPending.sort((a, b) => new Date(b.registrationDate) - new Date(a.registrationDate));
    completed.sort((a, b) => new Date(b.registrationDate) - new Date(a.registrationDate));

    setFilteredAccounting(filteredPending);
    setCompletedPayments(completed);
  }, [accountingData, searchTerm, paymentFilter]);

  const toggleInstallmentPayment = async (accountId, installmentNumber) => {
    try {
      const account = accountingData.find(acc => acc.id === accountId);
      const installment = account.installments.find(inst => inst.number === installmentNumber);
      const newPaidStatus = !installment.paid;
      const newPaidDate = newPaidStatus ? new Date().toISOString().split('T')[0] : null;

      // Update local state first for immediate UI feedback
      setAccountingData(prevData =>
        prevData.map(acc => {
          if (acc.id === accountId) {
            const updatedInstallments = acc.installments.map(inst => {
              if (inst.number === installmentNumber) {
                return {
                  ...inst,
                  paid: newPaidStatus,
                  paidDate: newPaidDate,
                };
              }
              return inst;
            });

            // Calculate totals
            const totalPaid = updatedInstallments.reduce(
              (sum, inst) => sum + (inst.paid ? inst.amount : 0),
              0
            );
            const remainingAmount = acc.agreedPrice - totalPaid;
            const paidCount = updatedInstallments.filter(inst => inst.paid).length;

            let paymentStatus = "pending";
            if (paidCount === updatedInstallments.length) {
              paymentStatus = "completed";
            } else if (paidCount > 0) {
              paymentStatus = "partial";
            }

            return {
              ...acc,
              installments: updatedInstallments,
              totalPaid: totalPaid,
              remainingAmount: remainingAmount,
              paymentStatus: paymentStatus,
            };
          }
          return acc;
        })
      );

      // Find the corresponding student to update
      const student = students.find(s => s.id === accountId);
      if (student) {
        // Create updated payment data structure
        const paymentData = student.paymentData || {};
        const installmentKey = `installment_${installmentNumber}`;

        const updatedPaymentData = {
          ...paymentData,
          [installmentKey]: {
            paid: newPaidStatus,
            paidDate: newPaidDate,
          }
        };

        // Update student in database with new payment data
        const updatedStudentData = {
          ...student,
          paymentData: updatedPaymentData,
        };

        await updateStudent(accountId, updatedStudentData);

        // Update local students array
        setStudents(prevStudents =>
          prevStudents.map(s =>
            s.id === accountId ? { ...s, paymentData: updatedPaymentData } : s
          )
        );
      }

      // Log the payment action
      await logAdminActivity("payment_marked", {
        studentName: account.studentName,
        installmentNumber: installmentNumber,
        amount: installment.amount,
        action: newPaidStatus ? "paid" : "unpaid",
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error updating payment:", error);
      // Reload data to sync with database in case of error
      loadAccountingData();
    }
  };

  const openStudentModal = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedStudent(null);
    setShowModal(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "#38a169";
      case "partial": return "#d69e2e";
      case "pending": return "#e53e3e";
      default: return "#718096";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed": return "Tamamlandı";
      case "partial": return "Kısmi Ödeme";
      case "pending": return "Ödeme Bekliyor";
      default: return "Bilinmeyen";
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Muhasebe verileri yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="accounting-section">
      <div className="section-header">
        <div className="header-content">
          <FaMoneyBillWave className="header-icon" />
          <div>
            <h2>Muhasebe Defteri</h2>
            <p>Öğrenci ödemelerini ve taksitleri takip edin</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card total-revenue">
          <FaDollarSign className="card-icon" />
          <div>
            <h3>Toplam Gelir</h3>
            <p>€{accountingData.reduce((sum, acc) => sum + acc.agreedPrice, 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="summary-card collected-revenue">
          <FaCheckCircle className="card-icon" />
          <div>
            <h3>Toplanan</h3>
            <p>€{accountingData.reduce((sum, acc) => sum + acc.totalPaid, 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="summary-card pending-revenue">
          <FaExclamationTriangle className="card-icon" />
          <div>
            <h3>Bekleyen</h3>
            <p>€{accountingData.reduce((sum, acc) => sum + acc.remainingAmount, 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Öğrenci adı ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <FaFilter className="filter-icon" />
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
          >
            <option value="all">Tüm Ödemeler</option>
            <option value="pending">Ödeme Bekliyor</option>
            <option value="partial">Kısmi Ödeme</option>
            <option value="completed">Tamamlandı</option>
          </select>
        </div>
      </div>

      {/* Accounting Table */}
      <div className="accounting-table">
        {filteredAccounting.length === 0 ? (
          <div className="no-data">
            <FaExclamationTriangle />
            <p>Henüz muhasebe verisi bulunmuyor.</p>
          </div>
        ) : (
          filteredAccounting.map((account) => (
            <div key={account.id} className="accounting-row">
              <div className="student-info">
                <div className="student-name">{account.studentName}</div>
                <div className="student-details">
                  <span>Anlaşılan: €{account.agreedPrice.toFixed(2)}</span>
                  <span>Taksit: {account.installmentCount}</span>
                  <span
                    className="payment-status"
                    style={{ color: getStatusColor(account.paymentStatus) }}
                  >
                    {getStatusText(account.paymentStatus)}
                  </span>
                </div>
              </div>

              <div className="installments-grid">
                {account.installments.map((installment) => (
                  <div
                    key={installment.number}
                    className={`installment-item ${installment.paid ? 'paid' : 'unpaid'}`}
                  >
                    <div className="installment-info">
                      <span className="installment-number">{installment.number}</span>
                      <span className="installment-amount">€{installment.amount.toFixed(2)}</span>
                      {installment.paid && installment.paidDate && (
                        <span className="paid-date">{installment.paidDate}</span>
                      )}
                    </div>
                    <button
                      className={`payment-toggle ${installment.paid ? 'paid' : 'unpaid'}`}
                      onClick={() => toggleInstallmentPayment(account.id, installment.number)}
                    >
                      {installment.paid ? <FaCheckCircle /> : <FaTimes />}
                    </button>
                  </div>
                ))}
              </div>

              <div className="account-summary">
                <div className="summary-item">
                  <span>Toplanan:</span>
                  <span className="paid-amount">€{account.totalPaid.toFixed(2)}</span>
                </div>
                <div className="summary-item">
                  <span>Kalan:</span>
                  <span className="remaining-amount">€{account.remainingAmount.toFixed(2)}</span>
                </div>
                <button
                  className="view-details-btn"
                  onClick={() => openStudentModal(account)}
                >
                  <FaEye /> Detay
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Completed Payments Section */}
      {completedPayments.length > 0 && (
        <div className="completed-payments-section">
          <div className="completed-header" onClick={() => setShowCompleted(!showCompleted)}>
            <div className="completed-title">
              <FaCheckCircle className="completed-icon" />
              <h3>Ödemesi Tamamlananlar ({completedPayments.length})</h3>
            </div>
            <div className="completed-summary">
              <span>Toplam: €{completedPayments.reduce((sum, acc) => sum + acc.agreedPrice, 0).toFixed(2)}</span>
              <button className="toggle-completed">
                {showCompleted ? <FaTimes /> : <FaEye />}
              </button>
            </div>
          </div>

          {showCompleted && (
            <div className="completed-table">
              {completedPayments.map((account) => (
                <div key={account.id} className="completed-row">
                  <div className="completed-student-info">
                    <div className="student-name">{account.studentName}</div>
                    <div className="student-details">
                      <span>Anlaşılan: €{account.agreedPrice.toFixed(2)}</span>
                      <span>Taksit: {account.installmentCount}</span>
                      <span className="payment-status completed-status">
                        Tamamlandı
                      </span>
                    </div>
                  </div>

                  <div className="completed-installments">
                    {account.installments.map((installment) => (
                      <div key={installment.number} className="completed-installment-item">
                        <div className="installment-info">
                          <span className="installment-number">{installment.number}</span>
                          <span className="installment-amount">€{installment.amount.toFixed(2)}</span>
                          {installment.paidDate && (
                            <span className="paid-date">{installment.paidDate}</span>
                          )}
                        </div>
                        <div className="completed-check">
                          <FaCheckCircle />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="completed-actions">
                    <button
                      className="view-details-btn completed-btn"
                      onClick={() => openStudentModal(account)}
                    >
                      <FaEye /> Detay
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal for Student Details */}
      {showModal && selectedStudent && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ödeme Detayları - {selectedStudent.studentName}</h3>
              <button className="close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="payment-overview">
                <div className="overview-card">
                  <h4>Genel Bakış</h4>
                  <div className="overview-grid">
                    <div>
                      <label>Anlaşılan Fiyat:</label>
                      <span>€{selectedStudent.agreedPrice.toFixed(2)}</span>
                    </div>
                    <div>
                      <label>Taksit Sayısı:</label>
                      <span>{selectedStudent.installmentCount}</span>
                    </div>
                    <div>
                      <label>Taksit Tutarı:</label>
                      <span>€{(selectedStudent.agreedPrice / selectedStudent.installmentCount).toFixed(2)}</span>
                    </div>
                    <div>
                      <label>Durum:</label>
                      <span style={{ color: getStatusColor(selectedStudent.paymentStatus) }}>
                        {getStatusText(selectedStudent.paymentStatus)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="installment-details">
                <h4>Taksit Detayları</h4>
                <div className="installment-list">
                  {selectedStudent.installments.map((installment) => (
                    <div
                      key={installment.number}
                      className={`installment-detail-item ${installment.paid ? 'paid' : 'unpaid'}`}
                    >
                      <div className="installment-header">
                        <span className="installment-title">
                          {installment.number}. Taksit
                        </span>
                        <span className="installment-amount">
                          €{installment.amount.toFixed(2)}
                        </span>
                        <button
                          className={`toggle-payment ${installment.paid ? 'paid' : 'unpaid'}`}
                          onClick={() => {
                            toggleInstallmentPayment(selectedStudent.id, installment.number);
                            // Update modal data
                            const updatedStudent = accountingData.find(acc => acc.id === selectedStudent.id);
                            setSelectedStudent(updatedStudent);
                          }}
                        >
                          {installment.paid ? 'Ödendi' : 'Ödenmedi'}
                        </button>
                      </div>
                      {installment.paid && installment.paidDate && (
                        <div className="payment-date">
                          <FaCalendarAlt />
                          Ödeme Tarihi: {installment.paidDate}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingSection;